import type { ReportType } from "../../hooks/useProgressReport";

export interface ReportHeaderProps {
  childName: string;
  periodLabel: string;
  reportType: ReportType;
  onTypeChange: (type: ReportType) => void;
  onExport?: () => void;
  onPrint?: () => void;
}

/**
 * Report header with child name, period selector, and action buttons.
 * Features a warm gradient background with playful styling.
 */
export function ReportHeader({
  childName,
  periodLabel,
  reportType,
  onTypeChange,
  onExport,
  onPrint,
}: ReportHeaderProps) {
  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-magic-100 via-fairy-50 to-sunshine-50 p-6 shadow-sm print:shadow-none print:p-4 print:rounded-none">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-sunshine-200/30 rounded-full blur-2xl transform translate-x-8 -translate-y-8 print:hidden" />
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-magic-200/30 rounded-full blur-xl transform -translate-x-6 translate-y-6 print:hidden" />

      <div className="relative z-10">
        {/* Top row with child name and actions */}
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-2xl">📊</span>
              <h1 className="font-display font-bold text-display-md text-neutral-800">
                Progress Report
              </h1>
            </div>
            <p className="font-body text-body-lg text-neutral-600">
              <span className="font-semibold text-magic-600">{childName}</span>
              <span className="mx-2 text-neutral-400">•</span>
              <span>{periodLabel}</span>
            </p>
          </div>

          {/* Actions - hidden in print */}
          <div className="flex items-center gap-2 print:hidden">
            {onPrint && (
              <button
                onClick={onPrint}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-cloud hover:bg-neutral-50 text-neutral-600 hover:text-neutral-800 font-body text-sm font-medium transition-colors shadow-sm"
                aria-label="Print report"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                <span className="hidden sm:inline">Print</span>
              </button>
            )}
            {onExport && (
              <button
                onClick={onExport}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-magic-500 hover:bg-magic-600 text-cloud font-body text-sm font-medium transition-colors shadow-sm"
                aria-label="Export as PDF"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="hidden sm:inline">Export PDF</span>
              </button>
            )}
          </div>
        </div>

        {/* Report type toggle */}
        <div className="inline-flex rounded-xl bg-cloud/60 backdrop-blur-sm p-1 shadow-sm print:hidden">
          <button
            onClick={() => onTypeChange("weekly")}
            className={`px-4 py-2 rounded-lg font-body text-sm font-medium transition-all ${
              reportType === "weekly"
                ? "bg-cloud text-magic-600 shadow-sm"
                : "text-neutral-600 hover:text-neutral-800"
            }`}
          >
            Weekly
          </button>
          <button
            onClick={() => onTypeChange("monthly")}
            className={`px-4 py-2 rounded-lg font-body text-sm font-medium transition-all ${
              reportType === "monthly"
                ? "bg-cloud text-magic-600 shadow-sm"
                : "text-neutral-600 hover:text-neutral-800"
            }`}
          >
            Monthly
          </button>
        </div>

        {/* Print-only report type label */}
        <div className="hidden print:block font-body text-sm text-neutral-500 mt-2">
          {reportType === "weekly" ? "Weekly Report" : "Monthly Report"}
        </div>
      </div>
    </div>
  );
}

export default ReportHeader;
